<script setup lang="ts">
import type { CallinglyCall } from '@/types/types'
import BaseButton from '@/components/BaseButton.vue'
import LeadTagsEditor from '@/components/LeadTagsEditor.vue'

defineProps<{
  status: string | null
  dispositionCountdown: number
  call: CallinglyCall | null
  isDialpadOpen: boolean
  openTab: string | null
  canRetry: boolean
}>()

const note = defineModel<string>('note')

const emit = defineEmits<{
  disposition: [status: string]
  retry: []
  'skip-note': []
  'save-note': []
  thankyou: []
  'add-note': []
  done: []
}>()

// Bootstrap .btn + .btn-default + .btn-disposition / .btn-note, as utilities
// (10px root, explicit px). Mirrors the shared button strings used app-wide.
const btnBase =
  'inline-block font-normal text-center whitespace-nowrap align-middle ' +
  'touch-manipulation cursor-pointer select-none border no-underline ' +
  'hover:no-underline focus:no-underline active:outline-0'
const btnDisposition =
  btnBase +
  ' border-[#ccc] rounded-[4px] pl-[20px] pr-[20px] pt-[9px] pb-[6px] text-[15px]' +
  ' leading-[1.42857143] text-[#073b4c] hover:text-[#333] hover:border-[#adadad]' +
  ' focus:text-[#333] focus:border-[#adadad] active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]'
const btnNote =
  'flex items-center justify-center w-[135px] px-[18px] py-[8px] text-[14px] font-normal ' +
  'leading-[1.42857143] text-center whitespace-nowrap touch-manipulation cursor-pointer ' +
  'select-none border rounded-[4px] no-underline active:outline-0 ' +
  'active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]'
const btnNotePrimary =
  btnNote +
  ' text-white bg-[#337ab7] border-[#2e6da4] hover:bg-[#286090] hover:border-[#204d74]' +
  ' focus:bg-[#286090] focus:border-[#204d74]'
const btnNoteSuccess =
  btnNote +
  ' text-white bg-[#5cb85c] border-[#4cae4c] hover:bg-[#449d44] hover:border-[#398439]' +
  ' focus:bg-[#449d44] focus:border-[#398439]'
</script>

<template>
  <div
    v-if="status == 'disposition' && !isDialpadOpen && !openTab"
    class="mt-[10px] mb-[30px] text-center"
  >
    <h3 class="text-center font-normal mb-[20px] mt-0">Call Result</h3>

    <button type="button" :class="btnDisposition" @click="emit('disposition', 'contacted')">
      <i class="material-icons text-[#5cb85c] text-[25px]!">how_to_reg</i
      ><span class="leading-[26px] align-top ml-[7px]">Contacted</span>
    </button>

    <button type="button" :class="btnDisposition" @click="emit('disposition', 'voicemail')">
      <i class="material-icons text-[#f0ad4e] text-[25px]!">voicemail</i>
      <span class="leading-[26px] align-top ml-[7px]">Voicemail</span>
    </button>

    <button type="button" :class="btnDisposition" @click="emit('disposition', 'missed')">
      <i class="material-icons text-[#d9534f] text-[25px]!">call_missed_outgoing</i>
      <span class="leading-[26px] align-top ml-[7px]">Missed</span>
    </button>
    <br />
    <button
      type="button"
      class="mt-[10px] pt-[5px]! pb-[2px]! pl-[10px]! pr-[15px]!"
      :class="btnDisposition"
      @click="emit('disposition', 'removed')"
    >
      <i class="material-icons text-[#d9534f] text-[25px]!">close</i>
      <span class="leading-[26px] align-top ml-[2px]">Remove</span>
    </button>

    <br />

    <button
      v-if="canRetry"
      type="button"
      class="mt-[5px]"
      :class="btnDisposition"
      @click="emit('retry')"
    >
      <i class="material-icons text-[#073b4c] text-[18px]! leading-[1.4]!">repeat</i>
      <span class="leading-[23px] align-top ml-[7px]">Retry</span>
    </button>

    <p v-if="dispositionCountdown <= 10" class="mt-[20px] text-[14px] text-[#666]">
      Closing in...<span class="font-bold text-[#073b4c]">{{ dispositionCountdown }}</span
      >s
    </p>
  </div>

  <div v-if="status == 'note' && !isDialpadOpen && !openTab" class="mt-4 px-4">
    <h3 class="text-center font-normal mb-[20px] mt-0">Add Note</h3>

    <div
      class="rounded-lg bg-white outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-blue-300"
    >
      <textarea
        id="note"
        name="note"
        v-model="note"
        rows="3"
        placeholder="Add a note..."
        class="block w-full resize-none bg-transparent px-4 py-4 text-[18px] leading-[1.42857143] text-gray-900 placeholder:text-gray-400"
      />
    </div>
    <div class="mb-6"></div>
    <div class="flex justify-end">
      <BaseButton type="button" variant="link" size="lg" @click="emit('skip-note')"
        >Skip</BaseButton
      >
      <BaseButton
        type="button"
        variant="primary"
        size="lg"
        :disabled="!note"
        @click="emit('save-note')"
      >
        Save
      </BaseButton>
    </div>
  </div>

  <LeadTagsEditor
    v-if="status == 'disposition2' && call?.id"
    :call-id="call.id"
    @done="emit('thankyou')"
  />

  <div v-if="status == 'thankyou' && !isDialpadOpen && !openTab">
    <h3 class="text-center">Thank you!</h3>
    <div class="mb-17"></div>

    <div class="flex justify-between mb-8 w-[300px] mx-auto">
      <button type="button" :class="btnNotePrimary" @click="emit('add-note')">
        <i class="material-icons mr-4 text-[20px]!">description</i>Add Note
      </button>
      <button type="button" :class="btnNoteSuccess" @click="emit('done')">
        <i class="material-icons mr-4 text-[20px]!">check</i>Done
      </button>
    </div>
  </div>
</template>
